import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router";
import { apiOvertimeReasonUpdate } from "@src/apis/labour-reason-update";
import { apiDRSOvertimeFetch } from "@src/apis/drs-overtime-fetch";
import { useAppStore } from "@src/AppStore";
import { useDRS } from "@src/modules/DRS/useDRS";

const reasonCodes = [
  "OT01 – B - Tender Allowance/Increased Productivity (Reduced Shifts)",
  "OT02 - C - Client Scope / Readiness / Instructions",
  "OT03 - C - Client Requirements / Testing",
  "OT04 - UB - Tender / Planning / Productivity Issues",
  "OT05 - UB - Weather / Environmental Conditions",
  "OT06 - UB - Safety Incident / Investigation",
  "OT07 - UB - Plant Production Constraints / Breakdown / Maintenance",
  "OT08 - UB - Logistics Constraints",
  "OT09 - UB - Mobile Plant / Equipment Constraints (On-Site)",
  "OT10 - UB - Labour / Staffing / Training Constraints",
];

const CompDRSOvertime = () => {
  const { id: drsSummaryId } = useParams<{ id: string }>();
  const { serialId } = useAppStore();
  const { drs } = useDRS();

  const [users, setUsers] = useState<any[]>([]);
  const [selectedReasons, setSelectedReasons] = useState<{ [overtimeId: number]: string }>({});
  const [applyToAllReason, setApplyToAllReason] = useState("");

  useEffect(() => {
    async function fetchData() {
      if (!drs?.jobDetails?.date || !drsSummaryId) return;

      const jobDate = new Date(drs.jobDetails.date);

      // Send plain date (YYYY-MM-DD) — no timezone conversion needed
      const dateStr = `${jobDate.getFullYear()}-${String(jobDate.getMonth() + 1).padStart(2, '0')}-${String(jobDate.getDate()).padStart(2, '0')}`;
      const start = dateStr;
      const end = dateStr;

      const res = await apiDRSOvertimeFetch({ drsSummaryId, start, end });
      setUsers(res.data?.labour || []);
    }

    fetchData();
  }, [drs.jobDetails.date, drsSummaryId, serialId]);

  const handleReasonChange = (overtimeId: number, value: string) => {
    setSelectedReasons((prev) => ({ ...prev, [overtimeId]: value }));
  };

  const handleApplyToAll = () => {
    const updated = Object.fromEntries(users.map((u) => [u.overtimeId, applyToAllReason]));
    setSelectedReasons(updated);
  };

  const handleSave = async () => {
    const updates = Object.entries(selectedReasons).map(([id, reasonCode]) => apiOvertimeReasonUpdate({ overtimeId: Number(id), reasonCode, serialId }));
    await Promise.all(updates);
    alert("Saved successfully");

    // Refresh the overtime validation in Summary component
    if (typeof window !== "undefined" && (window as any).refreshOvertimeReasons) {
      (window as any).refreshOvertimeReasons();
    }
  };

  const totalHours = users.reduce((sum, u) => sum + (u.hours || 0), 0);

  return (
    <Wrapper>
      <TopRow>
        <Spacer />
        <Select value={applyToAllReason} onChange={(e) => setApplyToAllReason(e.target.value)}>
          <option value="">Select reason</option>
          {reasonCodes.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </Select>
        <ApplyButton onClick={handleApplyToAll}>Apply to All</ApplyButton>
      </TopRow>

      <Table>
        <thead>
          <tr>
            <th>Employee Name</th>
            <th>Overtime Date</th>
            <th>Overtime Type</th>
            <th>Hours</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.overtimeId}>
              <td>{`${user.firstName || ""} ${user.lastName || ""}`}</td>
              <td>{new Date(user.overtimeDate).toLocaleDateString()}</td>
              <td>{user.overtimeType}</td>
              <td>{user.hours}</td>
              <td>
                <RowSelect value={selectedReasons[user.overtimeId] || user.overTimeReasonCode || ""} onChange={(e) => handleReasonChange(user.overtimeId, e.target.value)}>
                  <option value="">Select</option>
                  {reasonCodes.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </RowSelect>
              </td>
            </tr>
          ))}
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td></td>
            <td></td>
            <td>
              <strong>{totalHours}</strong>
            </td>
            <td></td>
          </tr>
        </tbody>
      </Table>

      <BottomRow>
        <SaveButton onClick={handleSave}>Save</SaveButton>
      </BottomRow>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-top: 20px;
  position: relative;
  min-height: 500px;
`;
const TopRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 20px;
  gap: 10px;
`;
const Spacer = styled.div`
  flex-grow: 1;
`;
const Select = styled.select`
  padding: 5px;
  min-width: 250px;
  height: 38px;
`;
const RowSelect = styled.select`
  padding: 5px;
  width: 100%;
`;
const ApplyButton = styled.button`
  background-color: #0a9688;
  color: white;
  padding: 5px 10px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  height: 38px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
  }
  th {
    background-color: #0a9688;
    color: white;
  }
`;
const BottomRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;
const SaveButton = styled.button`
  background-color: #0a9688;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  height: 38px;
`;

export default CompDRSOvertime;
